import { useEffect, useRef, useState } from "react";
import { Camera, ImagePlus, RotateCw, X, Loader2, AlertTriangle } from "lucide-react";
import { api, errMsg, fmtDT } from "../lib/api";

export function AuthImg({ id, size = "thumb", className = "", alt = "" }) {
  const [src, setSrc] = useState(null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let url;
    let alive = true;
    api.get(`/photos/${id}/${size}`, { responseType: "blob" })
      .then(({ data }) => {
        url = URL.createObjectURL(data);
        if (alive) setSrc(url);
      })
      .catch(() => alive && setErr(true));
    return () => {
      alive = false;
      if (url) URL.revokeObjectURL(url);
    };
  }, [id, size]);
  if (err) return <div className={`flex items-center justify-center bg-[#1a1a1a] text-xs text-neutral-500 ${className}`}>Unavailable</div>;
  if (!src) return <div className={`animate-pulse bg-[#1a1a1a] ${className}`} />;
  return <img src={src} alt={alt} className={`object-cover ${className}`} />;
}

export function PhotoViewer({ photo, onClose }) {
  if (!photo) return null;
  return (
    <div className="fixed inset-0 z-[70] bg-black/90 flex flex-col p-4" onClick={onClose} data-testid="photo-viewer">
      <button className="self-end p-2 text-white" aria-label="Close"><X className="w-6 h-6" /></button>
      <AuthImg id={photo.id} size="original" className="flex-1 w-full object-contain !bg-transparent" />
      <div className="text-xs text-neutral-400 font-mono mt-2 space-y-0.5" onClick={(e) => e.stopPropagation()}>
        <div>{photo.caption}</div>
        <div>Uploaded (server): {fmtDT(photo.uploaded_at)} by {photo.uploader_name}</div>
        <div>Capture time (device-reported, unverified): {photo.exif_time || (photo.device_time ? fmtDT(photo.device_time) : "not available")}</div>
      </div>
    </div>
  );
}

const TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX = 15 * 1024 * 1024;

export function PhotoSlot({ jobId, phase, slot, label, required, photos, locked, onUploaded, onView }) {
  const cam = useRef();
  const gal = useRef();
  const [queue, setQueue] = useState([]);
  const [caption, setCaption] = useState("");
  const mine = photos.filter((p) => p.phase === phase && p.slot === slot);

  const upload = async (item) => {
    setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, status: "uploading", progress: 0, error: "" } : x)));
    const fd = new FormData();
    fd.append("file", item.file);
    fd.append("phase", phase);
    fd.append("slot", slot);
    fd.append("caption", item.caption);
    fd.append("device_time", item.file.lastModified ? new Date(item.file.lastModified).toISOString() : "");
    try {
      await api.post(`/jobs/${jobId}/photos`, fd, {
        onUploadProgress: (e) => setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, progress: Math.round((e.loaded / (e.total || 1)) * 100) } : x))),
      });
      setQueue((q) => q.filter((x) => x.key !== item.key));
      onUploaded();
    } catch (e) {
      setQueue((q) => q.map((x) => (x.key === item.key ? { ...x, status: "failed", error: errMsg(e) } : x)));
    }
  };

  const pick = (e) => {
    const files = [...(e.target.files || [])];
    e.target.value = "";
    files.forEach((file) => {
      const item = { key: `${file.name}-${Date.now()}-${Math.random()}`, file, caption, status: "queued", progress: 0, error: "" };
      if (!TYPES.includes(file.type)) item.error = "Use JPEG, PNG or WebP (on iPhone choose Most Compatible camera format)";
      else if (file.size > MAX) item.error = "Photo is larger than 15 MB";
      if (item.error) {
        item.status = "failed";
        setQueue((q) => [...q, item]);
      } else {
        setQueue((q) => [...q, item]);
        upload(item);
      }
    });
    setCaption("");
  };

  return (
    <div className="rounded-xl border border-[#262626] bg-[#0F0F0F] p-3 space-y-2" data-testid={`photo-slot-${phase}-${slot}`}>
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">
          {label} {required && <span className="text-[#FF6B00] text-xs">required</span>}
        </div>
        <span className="text-xs font-mono text-neutral-500">{mine.length} saved</span>
      </div>
      {(mine.length > 0 || queue.length > 0) && (
        <div className="grid grid-cols-3 gap-2">
          {mine.map((p) => (
            <button key={p.id} type="button" onClick={() => onView(p)} className="relative aspect-square rounded-lg overflow-hidden border border-[#262626]" data-testid="saved-photo-thumb">
              <AuthImg id={p.id} className="w-full h-full" />
              <span className="absolute bottom-1 left-1 text-[9px] font-mono bg-black/70 px-1 rounded text-emerald-400">SAVED</span>
            </button>
          ))}
          {queue.map((q) => (
            <div key={q.key} className="relative aspect-square rounded-lg border border-[#333] flex flex-col items-center justify-center text-center p-1" data-testid="upload-queue-item">
              {q.status === "failed" ? (
                <>
                  <AlertTriangle className="w-4 h-4 text-red-400" />
                  <span className="text-[10px] text-red-400 line-clamp-3">{q.error || "Failed"}</span>
                  <div className="flex gap-1 mt-1">
                    {TYPES.includes(q.file.type) && q.file.size <= MAX && (
                      <button type="button" onClick={() => upload(q)} className="p-1 text-[#FF6B00]" data-testid="upload-retry-btn"><RotateCw className="w-4 h-4" /></button>
                    )}
                    <button type="button" onClick={() => setQueue((x) => x.filter((y) => y.key !== q.key))} className="p-1 text-neutral-500"><X className="w-4 h-4" /></button>
                  </div>
                </>
              ) : (
                <>
                  <Loader2 className="w-4 h-4 animate-spin text-[#FF6B00]" />
                  <span className="text-[11px] font-mono mt-1" data-testid="upload-progress">{q.progress}%</span>
                  <div className="w-4/5 h-1 bg-[#222] rounded mt-1"><div className="h-1 bg-[#FF6B00] rounded" style={{ width: `${q.progress}%` }} /></div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
      {!locked && (
        <>
          <input value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="Caption / condition note (optional)"
            className="w-full bg-transparent border-b border-[#262626] text-sm py-1.5 outline-none focus:border-[#FF6B00]" data-testid={`photo-caption-${phase}-${slot}`} />
          <div className="flex gap-2">
            <button type="button" onClick={() => cam.current.click()} className="flex-1 h-10 rounded-lg bg-[#FF6B00] text-white text-sm font-semibold flex items-center justify-center gap-1.5" data-testid={`photo-camera-btn-${phase}-${slot}`}>
              <Camera className="w-4 h-4" />Camera
            </button>
            <button type="button" onClick={() => gal.current.click()} className="flex-1 h-10 rounded-lg border border-[#333] text-sm flex items-center justify-center gap-1.5" data-testid={`photo-gallery-btn-${phase}-${slot}`}>
              <ImagePlus className="w-4 h-4" />Gallery
            </button>
          </div>
          <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={pick} data-testid={`photo-camera-input-${phase}-${slot}`} />
          <input ref={gal} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={pick} data-testid={`photo-file-input-${phase}-${slot}`} />
        </>
      )}
    </div>
  );
}
